import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import CRMTenantBar from './CRMTenantBar';

const API = '/api';
const PRIMARY = '#6366f1';
const SUCCESS = '#22c55e';
const DANGER = '#ef4444';
const BORDER = '#e5e7eb';
const BG = '#f8fafc';
const CARD = '#ffffff';
const TEXT = '#1e293b';
const MUTED = '#64748b';

function hdr() { return { Authorization: `Bearer ${localStorage.getItem('token')}` }; }

interface Tenant { id: number; company_name?: string; }

interface QueueMapping {
  queue_id: number;
  queue_name: string;
  connection_id: number | null;
  country_code: string;
  cache_ttl_seconds: number;
  screen_pop_object: string;
  screen_pop_enabled: boolean;
  auto_log_calls: boolean;
  auto_create_ticket: boolean;
  ticket_on_negative_sentiment: boolean;
  ticket_on_complaint: boolean;
}

const OBJECT_TYPES = ['Contact', 'Lead', 'Account', 'Any'];
const CACHE_TTLS = [
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 1800, label: '30 minutes' },
  { value: 3600, label: '1 hour' },
  { value: 14400, label: '4 hours' },
];

export default function CRMQueueMappingPage({ user, tenants = [] }: { user: any; tenants?: Tenant[] }) {
  const isSuperAdmin = !!(user?.role === 'super_admin' || user?.is_admin);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(
    isSuperAdmin ? (tenants?.[0]?.id ?? null) : (user?.tenant_id ?? null)
  );
  const tenantId = isSuperAdmin ? selectedTenantId : (user?.tenant_id ?? null);
  const qs = tenantId !== null ? `?tenant_id=${tenantId}` : '';

  const [mappings, setMappings] = useState<QueueMapping[]>([]);
  const [connections, setConnections] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<number | null>(null);
  const [msgs, setMsgs] = useState<Record<number, { text: string; type: 'ok' | 'err' }>>({});

  const load = useCallback(async () => {
    if (tenantId === null) { setMappings([]); setConnections([]); return; }
    setLoading(true);
    try {
      const [mRes, cRes] = await Promise.all([
        axios.get(`${API}/crm-integration/queue-mapping${qs}`, { headers: hdr() }),
        axios.get(`${API}/crm-integration/connections${qs}`, { headers: hdr() }),
      ]);
      setMappings(mRes.data.mappings || []);
      setConnections((cRes.data.connections || []).filter((c: any) => c.status === 'connected'));
    } catch (e) { } finally { setLoading(false); }
  }, [tenantId, qs]);

  useEffect(() => { load(); setMsgs({}); }, [load]);

  const update = (qid: number, key: keyof QueueMapping, value: any) => {
    setMappings(prev => prev.map(m => m.queue_id === qid ? { ...m, [key]: value } : m));
  };

  const save = async (m: QueueMapping) => {
    if (tenantId === null) return;
    setSaving(m.queue_id);
    try {
      await axios.post(`${API}/crm-integration/queue-mapping`, {
        tenant_id: tenantId,
        queue_id: m.queue_id,
        connection_id: m.connection_id || null,
        country_code: m.country_code || '+1',
        cache_ttl_seconds: m.cache_ttl_seconds || 900,
        screen_pop_object: m.screen_pop_object || 'Contact',
        screen_pop_enabled: m.screen_pop_enabled,
        auto_log_calls: m.auto_log_calls,
        auto_create_ticket: m.auto_create_ticket,
        ticket_on_negative_sentiment: m.ticket_on_negative_sentiment,
        ticket_on_complaint: m.ticket_on_complaint,
      }, { headers: hdr() });
      setMsgs(prev => ({ ...prev, [m.queue_id]: { text: 'Saved', type: 'ok' } }));
      setTimeout(() => setMsgs(prev => { const n = { ...prev }; delete n[m.queue_id]; return n; }), 2500);
    } catch (e: any) {
      setMsgs(prev => ({ ...prev, [m.queue_id]: { text: e?.response?.data?.error || 'Save failed', type: 'err' } }));
    } finally { setSaving(null); }
  };

  const Toggle = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <div onClick={onChange} style={{ width: 38, height: 20, borderRadius: 10, background: checked ? PRIMARY : '#cbd5e1', cursor: 'pointer', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 2, left: checked ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
    </div>
  );

  const s: Record<string, React.CSSProperties> = {
    page: { padding: 24, background: BG, minHeight: '100vh' },
    title: { fontSize: 22, fontWeight: 700, color: TEXT, margin: '0 0 4px' },
    sub: { color: MUTED, fontSize: 14, marginBottom: 20 },
    card: { background: CARD, borderRadius: 12, border: `1px solid ${BORDER}`, marginBottom: 16, overflow: 'hidden' },
    cardHead: { padding: '14px 18px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    qname: { fontSize: 15, fontWeight: 700, color: TEXT },
    cardBody: { padding: '16px 18px' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
    label: { fontSize: 12, fontWeight: 600, color: MUTED, display: 'block', marginBottom: 4 },
    select: { width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${BORDER}`, fontSize: 13, color: TEXT },
    input: { width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${BORDER}`, fontSize: 13, color: TEXT, boxSizing: 'border-box' as const },
    toggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${BG}` },
    toggleLabel: { fontSize: 13, color: TEXT },
    footer: { padding: '12px 18px', borderTop: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 10 },
    btn: { padding: '7px 18px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: PRIMARY, color: '#fff' },
  };

  const tenantName = isSuperAdmin && tenantId ? tenants.find(t => t.id === tenantId)?.company_name : null;

  return (
    <div style={s.page}>
      <h2 style={s.title}>CRM Queue Mapping</h2>
      <p style={s.sub}>Configure CRM behavior per queue{tenantName ? ` for ${tenantName}` : ''}.</p>

      <CRMTenantBar isSuperAdmin={isSuperAdmin} tenants={tenants} selectedTenantId={selectedTenantId} onSelect={(id) => { setSelectedTenantId(id); setMsgs({}); }} />

      {tenantId === null ? (
        <div style={{ textAlign: 'center', padding: 60, color: MUTED }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏢</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Select a tenant to configure their queue-CRM mappings</div>
        </div>
      ) : loading ? (
        <div style={{ color: MUTED, padding: 20 }}>Loading queues...</div>
      ) : mappings.length === 0 ? (
        <div style={{ color: MUTED, padding: 40, textAlign: 'center' }}>No queues found{tenantName ? ` for ${tenantName}` : ''}.</div>
      ) : (
        mappings.map(m => (
          <div key={m.queue_id} style={s.card}>
            <div style={s.cardHead}>
              <span style={s.qname}>{m.queue_name}</span>
              <span style={{ fontSize: 12, color: MUTED }}>Queue ID: {m.queue_id}</span>
            </div>
            <div style={s.cardBody}>
              <div style={s.grid}>
                <div>
                  <label style={s.label}>CRM Connection</label>
                  <select style={s.select} value={m.connection_id ?? ''} onChange={e => update(m.queue_id, 'connection_id', e.target.value ? parseInt(e.target.value) : null)}>
                    <option value="">— None —</option>
                    {connections.map(c => <option key={c.id} value={c.id}>{c.provider_key} · {c.connection_name || c.id}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>Screen Pop Object</label>
                  <select style={s.select} value={m.screen_pop_object || 'Contact'} onChange={e => update(m.queue_id, 'screen_pop_object', e.target.value)}>
                    {OBJECT_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>Country Code (for phone lookup)</label>
                  <input style={s.input} value={m.country_code || '+1'} onChange={e => update(m.queue_id, 'country_code', e.target.value)} placeholder="+1" />
                </div>
                <div>
                  <label style={s.label}>Contact Cache TTL</label>
                  <select style={s.select} value={m.cache_ttl_seconds || 900} onChange={e => update(m.queue_id, 'cache_ttl_seconds', parseInt(e.target.value))}>
                    {CACHE_TTLS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 8 }}>Automation</div>
                {([
                  { key: 'screen_pop_enabled', label: 'Screen pop on incoming call', desc: 'Open CRM contact record in agent screen automatically' },
                  { key: 'auto_log_calls', label: 'Auto-log calls to CRM', desc: 'Create call activity in CRM after each call' },
                  { key: 'auto_create_ticket', label: 'Auto-create ticket on every call', desc: 'Always open a case/ticket in CRM for this queue' },
                  { key: 'ticket_on_negative_sentiment', label: 'Create ticket on negative sentiment', desc: 'Requires AI sentiment analysis enabled on queue' },
                  { key: 'ticket_on_complaint', label: 'Create ticket when complaint detected', desc: 'Requires complaint detection on queue' },
                ] as Array<{ key: keyof QueueMapping; label: string; desc: string }>).map(row => (
                  <div key={row.key} style={s.toggleRow}>
                    <div>
                      <div style={s.toggleLabel}>{row.label}</div>
                      <div style={{ fontSize: 11, color: MUTED }}>{row.desc}</div>
                    </div>
                    <Toggle checked={!!m[row.key]} onChange={() => update(m.queue_id, row.key, !m[row.key])} />
                  </div>
                ))}
              </div>
            </div>
            <div style={s.footer}>
              <button style={s.btn} onClick={() => save(m)} disabled={saving === m.queue_id}>{saving === m.queue_id ? 'Saving...' : 'Save'}</button>
              {msgs[m.queue_id] && (
                <span style={{ fontSize: 13, color: msgs[m.queue_id].type === 'ok' ? SUCCESS : DANGER, fontWeight: 600 }}>{msgs[m.queue_id].text}</span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
